import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["color", "rotate", "rotateLock", "tabIndex", "verticalAlign", "size", "icon", "padding", "borderStyle", "borderWidth", "title", "onClick", "onMouseLeave", "onMouseEnter", "onFocus", "onBlur", "onKeyPress", "role", "id", "aria-label", "aria-labelledby"];
import styled from 'foundations-theming/utils';
import { useIconSize } from 'foundations-assets';
import { forwardRef } from 'react';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { useIconCircleContext } from '../IconCircle/IconCircleProvider';
import { deepMerge } from '../../utils/deepMerge/deepMerge';
import { useColorSwatchContext } from '../ColorSwatch/ColorSwatchProvider';
import { jsx as _jsx } from "react/jsx-runtime";
const StyledIconHolder = styled.span.withConfig({
  displayName: "IconHolder__StyledIconHolder",
  componentId: "sc-111iq24-0"
})(({
  $rotate,
  $isIconCircle,
  $isLegacyIconCircle,
  $verticalAlign,
  $size,
  $cssStyles,
  $styleOverrides
}) => {
  return deepMerge(Object.assign({
    display: 'inline-flex',
    fontSize: $size != null ? useIconSize($size) : undefined,
    lineHeight: '1',
    // The unitless value isn't tokenized because it's relative to the tokenized font size
    transform: $rotate != null ? `rotate(${$rotate}deg)` : undefined,
    userSelect: 'none',
    position: 'relative',
    verticalAlign: $verticalAlign,
    /*
     * Padding defaults to 0.5em to match UIIconHolder or 0.75em to match UIIconCircle, but will be overridden if padding is passed in via `$cssStyles`.
     * This non-standard default value approach is used to default to a non-token value without exposing non-tokens to consumers.
     * The em values aren't tokenized because they're relative to the tokenized font size.
     */
    paddingBlock: $isIconCircle ? '0.75em' : '0.5em',
    paddingInline: $isIconCircle ? '0.75em' : '0.5em'
  }, $cssStyles, $isLegacyIconCircle ? {
    display: 'inline-flex',
    alignItems: 'center',
    blockSize: '2em',
    // The em value isn't tokenized because it's relative to the tokenized font size
    justifyContent: 'center',
    inlineSize: '2em' // The em value isn't tokenized because it's relative to the tokenized font size
  } : undefined), $styleOverrides);
});
export const StyledIconWrapper = styled.span.withConfig({
  displayName: "IconHolder__StyledIconWrapper",
  componentId: "sc-111iq24-1"
})(({
  $rotate,
  $rotateLock
}) => {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    blockSize: '1em',
    // The em value isn't tokenized because it's relative to the tokenized font size
    inlineSize: '1em',
    // The em value isn't tokenized because it's relative to the tokenized font size
    margin: '0.125em',
    // Allows us to keep the same size as `UIIconHolder` and also keep the icons centered
    transform: $rotate != null && !$rotateLock ? `rotate(${$rotate * -1}deg)` : undefined
  };
});
export const IconHolder = /*#__PURE__*/forwardRef((_ref, ref) => {
  var _iconCircleContext$is, _colorSwatchContext$i;
  let {
      color,
      rotate = 0,
      rotateLock,
      tabIndex,
      verticalAlign = '-0.1em',
      // Aligns the icon with text by default
      size,
      icon: Icon,
      padding,
      borderStyle = 'solid',
      borderWidth = '0',
      title,
      onClick,
      onMouseLeave,
      onMouseEnter,
      onFocus,
      onBlur,
      onKeyPress,
      role,
      id,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    tokenProps,
    domProps
  } = filterProps(Object.assign({
    padding,
    borderStyle,
    borderWidth
  }, rest));
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const iconCircleContext = useIconCircleContext();
  const colorSwatchContext = useColorSwatchContext();
  return /*#__PURE__*/_jsx(StyledIconHolder, Object.assign({
    ref: ref,
    $rotate: rotate,
    $isIconCircle: !!iconCircleContext,
    $isLegacyIconCircle: (_iconCircleContext$is = iconCircleContext === null || iconCircleContext === void 0 ? void 0 : iconCircleContext.isLegacy) !== null && _iconCircleContext$is !== void 0 ? _iconCircleContext$is : false,
    $verticalAlign: verticalAlign,
    $size: size,
    $cssStyles: cssStyles,
    $styleOverrides: (_colorSwatchContext$i = colorSwatchContext === null || colorSwatchContext === void 0 ? void 0 : colorSwatchContext.iconCircleStyleOverrides) !== null && _colorSwatchContext$i !== void 0 ? _colorSwatchContext$i : {},
    tabIndex: tabIndex,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    title: title,
    onClick: onClick,
    onMouseLeave: onMouseLeave,
    onMouseEnter: onMouseEnter,
    onFocus: onFocus,
    onBlur: onBlur,
    onKeyPress: onKeyPress,
    role: role,
    id: id
  }, domProps, {
    children: /*#__PURE__*/_jsx(StyledIconWrapper, {
      $rotate: rotate,
      $rotateLock: rotateLock,
      children: /*#__PURE__*/_jsx(Icon, {
        color: color,
        size: size
      })
    })
  }));
});